import { useState, useEffect, useCallback, useMemo } from 'react';
import { useUser } from '@/providers/UserContext';
import { useApi } from '@/providers/ApiContext';
import { getUsageMetric } from '@/actions/usage';
import UsageMetricsDisplay from './UsageMetricsDisplay';
import UserActivityGrid from './UserActivityGrid';
import CHAT_TYPES from '../../chatTypes';
import INSPECTION_TYPES from '../../inspectionTypes';
import type { UsageMetricData } from '@/types';

interface MetricConfig {
  key: string;
  label: string;
  metric: string;
  aggregation: string;
  dimensions: string[];
  filter: Record<string, unknown>;
}

interface UsageDataResult {
  key: string;
  data: UsageMetricData | null;
  error?: string;
}

function AccQsureAdminUsage(): React.JSX.Element {
  const user = useUser();
  const api = useApi();
  const [usageData, setUsageData] = useState<Record<string, UsageDataResult>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Calculate date range (7 days ago to today) - memoized to prevent infinite re-renders
  const { startDate, endDate } = useMemo(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 7);
    return { startDate: start, endDate: end };
  }, []);

  // Define all metrics to fetch
  const metrics: MetricConfig[] = useMemo(
    () => [
      {
        key: 'voyager_chats',
        label: 'Voyager Chat Responses',
        metric: 'chat_responses',
        aggregation: 'count',
        dimensions: ['date', 'rating'],
        filter: { type: CHAT_TYPES.VOYAGER },
      },
      {
        key: 'quartermaster_chats',
        label: 'Quartermaster Chat Responses',
        metric: 'chat_responses',
        aggregation: 'count',
        dimensions: ['date', 'rating'],
        filter: { type: CHAT_TYPES.QUARTERMASTER },
      },
      {
        key: 'document_evaluation_checks',
        label: 'Document Evaluation Checks',
        metric: 'inspection_checks',
        aggregation: 'count',
        dimensions: ['date', 'compliant'],
        filter: { type: INSPECTION_TYPES.PRELIMINARY },
      },
      {
        key: 'internal_audit_checks',
        label: 'Internal Audit Checks',
        metric: 'inspection_checks',
        aggregation: 'count',
        dimensions: ['date', 'compliant'],
        filter: { type: INSPECTION_TYPES.EFFECTIVE },
      },
    ],
    [],
  );

  const fetchUsageData = useCallback(async (): Promise<void> => {
    if (!api) return;

    setLoading(true);
    setError(null);

    try {
      const dataPromises = metrics.map(async (metricConfig): Promise<UsageDataResult> => {
        try {
          const response = await getUsageMetric(
            api,
            metricConfig.metric,
            metricConfig.aggregation,
            startDate.toISOString().split('T')[0], // Format as YYYY-MM-DD
            endDate.toISOString().split('T')[0], // Format as YYYY-MM-DD
            metricConfig.dimensions,
            metricConfig.filter,
          );
          return { key: metricConfig.key, data: response as unknown as UsageMetricData };
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'Unknown error';
          console.error('Error fetching metric:', metricConfig.label, err);
          return { key: metricConfig.key, data: null, error: errorMessage };
        }
      });

      const results = await Promise.all(dataPromises);
      const dataObject: Record<string, UsageDataResult> = {};
      results.forEach((result) => {
        dataObject[result.key] = result;
      });

      setUsageData(dataObject);
    } catch (err) {
      console.error('Error fetching usage data:', err);
      setError('Failed to load usage data');
    } finally {
      setLoading(false);
    }
  }, [api, startDate, endDate, metrics]);

  useEffect(() => {
    if (user?.entity_id) {
      fetchUsageData();
    }
  }, [user?.entity_id, fetchUsageData]);

  if (loading) {
    return <div>Loading usage metrics...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div>
      {/* Existing Usage Metrics */}
      {metrics.map((metricConfig) => {
        const metricData = usageData[metricConfig.key];
        const hasError = metricData?.error;
        const data = hasError ? null : metricData?.data || null;

        return (
          <UsageMetricsDisplay
            key={metricConfig.key}
            data={data}
            label={metricConfig.label}
            loading={false}
            error={
              hasError
                ? `Failed to load ${metricConfig.label}: ${hasError}`
                : null
            }
          />
        );
      })}

      {/* User Activity Grid */}
      {api && <UserActivityGrid api={api} startDate={startDate} endDate={endDate} />}
    </div>
  );
}

export default AccQsureAdminUsage;
